import { useState, useRef } from 'react';
import cenowkiBufferService from '../../services/cenowkiBufferService';
import toast from 'react-hot-toast';

const useBarcodeScanner = ({ allProducts, selectedProducts, setSelectedProducts, selectedLocation, labelBuffer, loadBuffer }) => {
  const [showBarcodeScanner, setShowBarcodeScanner] = useState(false);
  const [scannedBarcodes, setScannedBarcodes] = useState([]);
  const [barcodeInput, setBarcodeInput] = useState('');
  const [scannerFilterActive, setScannerFilterActive] = useState(false);

  const scannerInputRef = useRef(null);

  const handleBarcodeInput = (e) => {
    if (e.key === 'Enter' && barcodeInput.trim()) {
      const code = barcodeInput.trim();
      handleScanCode(code);
      setBarcodeInput('');
      e.preventDefault();
      if (scannerInputRef.current) {
        scannerInputRef.current.focus();
      }
    }
  };

  const handleScanCode = (code) => {
    const lowerCode = code.toLowerCase();

    const foundProduct = allProducts.find(p => {
      const productEAN = p.ean?.trim().toLowerCase() || '';
      const productCode = p.kod_produktu?.trim().toLowerCase() || '';
      return productEAN === lowerCode || productCode === lowerCode;
    });

    if (foundProduct) {
      if (!scannedBarcodes.includes(code)) {
        setScannedBarcodes(prev => [...prev, code]);
      }

      setSelectedProducts(prev => {
        const newSet = new Set(prev);
        newSet.add(foundProduct.id);
        return newSet;
      });

      setScannerFilterActive(true);

      // Sound effect
      try {
        const audio = new Audio('data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdH2djJdwWFpqgI+Ne2xhZ3qHiIF5cW1yeIGBfXZzcHR7fn18eXZ1dnh7fHx6eHZ2d3l7fHx7eXh3d3h5ent7e3p5eHh5ent7e3p5eHl5enp7e3p5eXl5enp6enp5eXl5enp6');
        audio.volume = 0.3;
        audio.play().catch(() => {});
      } catch (e) {}

      return { found: true, product: foundProduct };
    } else {
      try {
        const audio = new Audio('data:audio/wav;base64,UklGRl9vT19teleGVm...');
        audio.volume = 0.3;
        audio.play().catch(() => {});
      } catch (e) {}

      return { found: false, code };
    }
  };

  const handleAddBarcode = () => {
    const code = barcodeInput.trim();
    if (code) {
      handleScanCode(code);
    }
    setBarcodeInput('');
    if (scannerInputRef.current) {
      scannerInputRef.current.focus();
    }
  };

  const handleRemoveBarcode = (code) => {
    setScannedBarcodes(prev => prev.filter(c => c !== code));

    const lowerCode = code.toLowerCase();
    const product = allProducts.find(p => {
      const productEAN = p.ean?.trim().toLowerCase() || '';
      const productCode = p.kod_produktu?.trim().toLowerCase() || '';
      return productEAN === lowerCode || productCode === lowerCode;
    });

    if (product) {
      setSelectedProducts(prev => {
        const newSet = new Set(prev);
        newSet.delete(product.id);
        return newSet;
      });
    }
  };

  const handleClearAllBarcodes = () => {
    scannedBarcodes.forEach(code => {
      const lowerCode = code.toLowerCase();
      const product = allProducts.find(p => {
        const productEAN = p.ean?.trim().toLowerCase() || '';
        const productCode = p.kod_produktu?.trim().toLowerCase() || '';
        return productEAN === lowerCode || productCode === lowerCode;
      });
      if (product) {
        setSelectedProducts(prev => {
          const newSet = new Set(prev);
          newSet.delete(product.id);
          return newSet;
        });
      }
    });

    setScannedBarcodes([]);
    setScannerFilterActive(false);
  };

  const handleApplyScannerFilter = (setCurrentPage) => {
    if (scannedBarcodes.length > 0) {
      setScannerFilterActive(true);
      setShowBarcodeScanner(false);
      setCurrentPage(1);
    }
  };

  const handlePasteBarcodes = (e) => {
    const pastedText = e.clipboardData.getData('text');
    const lines = pastedText.split(/[\n\r,;]+/).map(l => l.trim()).filter(l => l);

    lines.forEach(code => {
      if (code) {
        handleScanCode(code);
      }
    });

    e.preventDefault();
  };

  const handleAddScannedToBuffer = async () => {
    if (!selectedLocation?.id) {
      toast.error('Proszę najpierw wybrać magazyn');
      return;
    }

    const scannedProducts = allProducts.filter(p => {
      const productEAN = p.ean?.trim().toLowerCase() || '';
      const productCode = p.kod_produktu?.trim().toLowerCase() || '';
      return scannedBarcodes.some(code => {
        const lowerCode = code.toLowerCase();
        return productEAN === lowerCode || productCode === lowerCode;
      });
    });

    if (scannedProducts.length > 0) {
      const existingIds = new Set(labelBuffer.map(item => item.id));
      const toAdd = scannedProducts.filter(item => !existingIds.has(item.id));

      let addedCount = 0;
      for (const product of toAdd) {
        const result = await cenowkiBufferService.addToBuffer(selectedLocation.id, product, 'scanner');
        if (result.success) addedCount++;
      }

      await loadBuffer();
      toast.success(`Dodano ${addedCount} produktów do bufora cenówek`);
    } else {
      toast.error('Nie znaleziono produktów dla zeskanowanych kodów');
    }
  };

  return {
    showBarcodeScanner,
    setShowBarcodeScanner,
    scannedBarcodes,
    barcodeInput,
    setBarcodeInput,
    scannerFilterActive,
    setScannerFilterActive,
    scannerInputRef,
    handleBarcodeInput,
    handleScanCode,
    handleAddBarcode,
    handleRemoveBarcode,
    handleClearAllBarcodes,
    handleApplyScannerFilter,
    handlePasteBarcodes,
    handleAddScannedToBuffer,
  };
};

export default useBarcodeScanner;
